import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigService, DocumentService, sharedApiClient } from '../fetcher';
import { codesEqualIgnoringTerms, mergeCodeMapWithDelta, normalizeCodeMap } from './configCodes';
import { EMAIL_JOIN_DEFAULT, DOCUMENT_TYPE_FIELD_DEFAULT } from './configConstants';
import useConfigDocument from './useConfigDocument';
import useConfiguredCodeList from './useConfiguredCodeList';
import useMeasurementCodes from './useMeasurementCodes';
import { useNotifications } from '@medprompt/shared';

const initialPreviousState = {
  retentionDays: 7,
  emailJoinType: EMAIL_JOIN_DEFAULT,
  shouldIncludeReceivedAt: false,
  shouldSendEmailSuccessAck: false,
  shouldExtractExtendedMeasurements: false,
  shouldBundleBloodPressureReadings: false,
  defaultDocumentTypeField: DOCUMENT_TYPE_FIELD_DEFAULT,
  selectedTypes: [],
  measurementCodes: {},
  clinicalDepartmentCodes: {},
  defaultClinicalDepartment: null,
  letterTypeCodes: {},
  defaultLetterTypeCode: null,
  documentContentTypeToGuidance: {},
  titleComponents: []
};

const useConfigForm = () => {
  const { addNotification } = useNotifications();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [shouldExtractExtendedMeasurements, setShouldExtractExtendedMeasurements] = useState(false);
  const [shouldBundleBloodPressureReadings, setShouldBundleBloodPressureReadings] = useState(false);
  const [documentContentTypeToGuidance, setDocumentContentTypeToGuidance] = useState({});
  const [guidanceChanges, setGuidanceChanges] = useState({});
  const [previousState, setPreviousState] = useState(initialPreviousState);
  const [isTemplateManagerModalOpen, setIsTemplateManagerModalOpen] = useState(false);

  const configManager = useMemo(() => new ConfigService(sharedApiClient), []);
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);

  const documentSection = useConfigDocument();
  const clinicalDepartments = useConfiguredCodeList({
    tableId: 'ClinicalDepartmentsConfigTable',
    baselineCodes: previousState.clinicalDepartmentCodes,
    isEditing: isEditingConfig,
    saving,
    addNotification,
    duplicateNotificationId: 'clinical-dept-dup-add',
    duplicateMessage: 'That clinical department code is already in the list.',
    bulkActionKey: 'removeClinicalDept',
  });
  const letterTypes = useConfiguredCodeList({
    tableId: 'LetterTypesConfigTable',
    baselineCodes: previousState.letterTypeCodes,
    isEditing: isEditingConfig,
    saving,
    addNotification,
    duplicateNotificationId: 'letter-type-dup-add',
    duplicateMessage: 'That letter type code is already in the list.',
    bulkActionKey: 'removeLetterType',
  });
  const measurements = useMeasurementCodes({
    baselineCodes: previousState.measurementCodes,
    isEditing: isEditingConfig,
    saving,
  });

  const handleCodeSearch = useCallback(async (query, callback) => {
    try {
      const results = await documentManager.searchAllCodes(query);
      callback(results);
    } catch (searchError) {
      console.error('Error searching measurement codes:', searchError);
      callback([]);
    }
  }, [documentManager]);

  const applyGeneratorFlags = useCallback((configData) => {
    const shouldExtractExtendedMeasurementsValue =
      configData?.generatorConfig?.shouldExtractExtendedMeasurements === true;
    setShouldExtractExtendedMeasurements(shouldExtractExtendedMeasurementsValue);

    const shouldBundleBloodPressureReadingsValue =
      configData?.generatorConfig?.shouldBundleBloodPressureReadings === true;
    setShouldBundleBloodPressureReadings(shouldBundleBloodPressureReadingsValue);

    const types = configData?.generatorConfig?.includedCodeTypes || [];
    setSelectedTypes(types);

    const guidanceMap = configData?.generatorConfig?.documentContentTypeToGuidance || {};
    setDocumentContentTypeToGuidance({ ...guidanceMap });
    setGuidanceChanges({});

    return {
      shouldExtractExtendedMeasurements: shouldExtractExtendedMeasurementsValue,
      shouldBundleBloodPressureReadings: shouldBundleBloodPressureReadingsValue,
      selectedTypes: [...types],
      documentContentTypeToGuidance: { ...guidanceMap },
    };
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const configData = await configManager.fetchConfig();

      if (!configData) {
        throw new Error('No configuration data received');
      }

      setConfig(configData);

      const documentSnapshot = documentSection.applyFetched(configData);
      const generatorSnapshot = applyGeneratorFlags(configData);
      const measurementSnapshot = measurements.applyFetched(configData?.generatorConfig?.measurementTypeToCode);
      const departmentSnapshot = clinicalDepartments.applyFetched(
        configData?.generatorConfig?.codeToClinicalDepartment,
        configData?.generatorConfig?.defaultClinicalDepartment
      );
      const letterSnapshot = letterTypes.applyFetched(
        configData?.generatorConfig?.codeToLetterType,
        configData?.generatorConfig?.defaultLetterTypeCode
      );

      setPreviousState({
        retentionDays: documentSnapshot.retentionDays,
        emailJoinType: documentSnapshot.emailJoinType,
        shouldIncludeReceivedAt: documentSnapshot.shouldIncludeReceivedAt,
        shouldSendEmailSuccessAck: documentSnapshot.shouldSendEmailSuccessAck,
        shouldExtractExtendedMeasurements: generatorSnapshot.shouldExtractExtendedMeasurements,
        shouldBundleBloodPressureReadings: generatorSnapshot.shouldBundleBloodPressureReadings,
        defaultDocumentTypeField: documentSnapshot.defaultDocumentTypeField,
        selectedTypes: generatorSnapshot.selectedTypes,
        measurementCodes: measurementSnapshot.measurementCodes,
        clinicalDepartmentCodes: departmentSnapshot.codes,
        defaultClinicalDepartment: departmentSnapshot.defaultCode,
        letterTypeCodes: letterSnapshot.codes,
        defaultLetterTypeCode: letterSnapshot.defaultCode,
        documentContentTypeToGuidance: generatorSnapshot.documentContentTypeToGuidance,
        titleComponents: documentSnapshot.titleComponents
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching configuration:', err);
      setError(err.message || 'Failed to load configuration');
      setLoading(false);
    }
  }, [
    applyGeneratorFlags,
    clinicalDepartments,
    configManager,
    documentSection,
    letterTypes,
    measurements,
  ]);

  useEffect(() => {
    fetchConfig();
    // Fetch once on mount. Later saves call fetchConfig directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGuidanceChange = useCallback(
    (key, text) => {
      const trimmed = text.trim();
      const baseline = (previousState.documentContentTypeToGuidance?.[key] ?? '').trim();
      setGuidanceChanges((prevCh) => {
        const next = { ...prevCh };
        if (trimmed === baseline) {
          delete next[key];
        } else if (trimmed === '') {
          if (baseline) {
            next[key] = null;
          } else {
            delete next[key];
          }
        } else {
          next[key] = trimmed;
        }
        return next;
      });
      setDocumentContentTypeToGuidance((prev) => {
        const next = { ...prev };
        if (trimmed === '') {
          delete next[key];
        } else {
          next[key] = text;
        }
        return next;
      });
    },
    [previousState.documentContentTypeToGuidance]
  );

  const handleGuidanceClear = useCallback(
    (key) => {
      handleGuidanceChange(key, '');
    },
    [handleGuidanceChange]
  );

  const toggleSearchType = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const capturePreviousState = () => {
    const documentSnapshot = documentSection.snapshot();
    const departmentSnapshot = clinicalDepartments.snapshot();
    const letterSnapshot = letterTypes.snapshot();
    const measurementSnapshot = measurements.snapshot();
    setPreviousState({
      retentionDays: documentSnapshot.retentionDays,
      emailJoinType: documentSnapshot.emailJoinType,
      shouldIncludeReceivedAt: documentSnapshot.shouldIncludeReceivedAt,
      shouldSendEmailSuccessAck: documentSnapshot.shouldSendEmailSuccessAck,
      shouldExtractExtendedMeasurements,
      shouldBundleBloodPressureReadings,
      defaultDocumentTypeField: documentSnapshot.defaultDocumentTypeField,
      selectedTypes: [...selectedTypes],
      measurementCodes: measurementSnapshot.measurementCodes,
      clinicalDepartmentCodes: departmentSnapshot.codes,
      defaultClinicalDepartment: departmentSnapshot.defaultCode,
      letterTypeCodes: letterSnapshot.codes,
      defaultLetterTypeCode: letterSnapshot.defaultCode,
      documentContentTypeToGuidance: { ...documentContentTypeToGuidance },
      titleComponents: documentSnapshot.titleComponents
    });
  };

  const handleEditToggle = () => {
    if (isEditingConfig) {
      setIsEditingConfig(false);
      measurements.clearChanges();
      documentSection.setEditingTitleComponent(null);
      clinicalDepartments.closeEditors();
      letterTypes.closeEditors();
    } else {
      setIsEditingConfig(true);
      measurements.setEditingMeasurement(null);
      documentSection.setEditingTitleComponent(null);
      // When starting to edit, capture the current state
      capturePreviousState();
    }
  };

  const handleCancel = () => {
    documentSection.restore(previousState);
    setSelectedTypes(previousState.selectedTypes);
    setShouldExtractExtendedMeasurements(previousState.shouldExtractExtendedMeasurements ?? false);
    setShouldBundleBloodPressureReadings(previousState.shouldBundleBloodPressureReadings ?? false);
    measurements.restore(previousState.measurementCodes);
    clinicalDepartments.restore(
      previousState.clinicalDepartmentCodes,
      previousState.defaultClinicalDepartment
    );
    letterTypes.restore(
      previousState.letterTypeCodes,
      previousState.defaultLetterTypeCode
    );
    setDocumentContentTypeToGuidance({ ...(previousState.documentContentTypeToGuidance || {}) });
    setGuidanceChanges({});
    setIsEditingConfig(false);
  };

  const applySavedConfig = (updateData, includeTemplates) => {
    const patch = (prev) => ({
      ...prev,
      ...(updateData.documentConfig && {
        documentConfig: {
          ...prev.documentConfig,
          ...(updateData.documentConfig.maxFiledAwayAge !== undefined && { maxFiledAwayAge: updateData.documentConfig.maxFiledAwayAge }),
          ...(updateData.documentConfig.titleComponents !== undefined && { titleComponents: updateData.documentConfig.titleComponents }),
          ...(updateData.documentConfig.emailJoinType !== undefined && { emailJoinType: updateData.documentConfig.emailJoinType }),
          ...(updateData.documentConfig.shouldIncludeReceivedAt !== undefined && {
            shouldIncludeReceivedAt: updateData.documentConfig.shouldIncludeReceivedAt
          }),
          ...(updateData.documentConfig.shouldSendEmailSuccessAck !== undefined && {
            shouldSendEmailSuccessAck: updateData.documentConfig.shouldSendEmailSuccessAck
          }),
          ...(updateData.documentConfig.defaultDocumentTypeField !== undefined && {
            defaultDocumentTypeField: updateData.documentConfig.defaultDocumentTypeField
          }),
          ...(includeTemplates && updateData.documentConfig.patientCommTemplates !== undefined && {
            patientCommTemplates: updateData.documentConfig.patientCommTemplates
          })
        }
      }),
      ...(updateData.generatorConfig && {
        generatorConfig: (() => {
          const prevGc = prev.generatorConfig || {};
          const nextGc = { ...prevGc };
          if (updateData.generatorConfig.includedCodeTypes) {
            nextGc.includedCodeTypes = selectedTypes;
          }
          if (updateData.generatorConfig.measurementTypeToCode != null) {
            nextGc.measurementTypeToCode = {
              ...prevGc.measurementTypeToCode,
              ...updateData.generatorConfig.measurementTypeToCode
            };
          }
          if (updateData.generatorConfig.codeToClinicalDepartment != null) {
            nextGc.codeToClinicalDepartment = mergeCodeMapWithDelta(
              normalizeCodeMap(prevGc.codeToClinicalDepartment),
              updateData.generatorConfig.codeToClinicalDepartment
            );
          }
          if (updateData.generatorConfig.defaultClinicalDepartment !== undefined) {
            nextGc.defaultClinicalDepartment = updateData.generatorConfig.defaultClinicalDepartment;
          }
          if (updateData.generatorConfig.codeToLetterType != null) {
            nextGc.codeToLetterType = mergeCodeMapWithDelta(
              normalizeCodeMap(prevGc.codeToLetterType),
              updateData.generatorConfig.codeToLetterType
            );
          }
          if (updateData.generatorConfig.defaultLetterTypeCode !== undefined) {
            nextGc.defaultLetterTypeCode = updateData.generatorConfig.defaultLetterTypeCode;
          }
          if (updateData.generatorConfig.shouldExtractExtendedMeasurements !== undefined) {
            nextGc.shouldExtractExtendedMeasurements = updateData.generatorConfig.shouldExtractExtendedMeasurements;
          }
          if (updateData.generatorConfig.shouldBundleBloodPressureReadings !== undefined) {
            nextGc.shouldBundleBloodPressureReadings = updateData.generatorConfig.shouldBundleBloodPressureReadings;
          }
          if (updateData.generatorConfig.documentContentTypeToGuidance != null) {
            nextGc.documentContentTypeToGuidance = mergeCodeMapWithDelta(
              prevGc.documentContentTypeToGuidance || {},
              updateData.generatorConfig.documentContentTypeToGuidance
            );
          }
          return nextGc;
        })()
      })
    });
    setConfig(patch);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      let updateData = {};

      const documentUpdate = documentSection.buildUpdate(previousState, addNotification);
      if (documentUpdate.aborted) {
        setSaving(false);
        return;
      }
      if (documentUpdate.changed) {
        updateData.documentConfig = documentUpdate.update;
      }

      const typesChanged = JSON.stringify(selectedTypes) !== JSON.stringify(previousState.selectedTypes);
      const defaultDeptBaseline = previousState.defaultClinicalDepartment || null;
      const defaultDeptChanged = !codesEqualIgnoringTerms(clinicalDepartments.defaultCode, defaultDeptBaseline);
      const defaultLetterBaseline = previousState.defaultLetterTypeCode || null;
      const defaultLetterChanged = !codesEqualIgnoringTerms(letterTypes.defaultCode, defaultLetterBaseline);
      const shouldExtractExtendedMeasurementsChanged =
        shouldExtractExtendedMeasurements !== previousState.shouldExtractExtendedMeasurements;
      const shouldBundleBloodPressureReadingsChanged =
        shouldBundleBloodPressureReadings !== previousState.shouldBundleBloodPressureReadings;
      const deptDeltaApplied = Object.keys(clinicalDepartments.changes).length > 0;
      const letterDeltaApplied = Object.keys(letterTypes.changes).length > 0;
      const measurementDeltaApplied = Object.keys(measurements.measurementChanges).length > 0;
      const guidanceDeltaApplied = Object.keys(guidanceChanges).length > 0;

      const generatorConfigUpdate = {};

      if (typesChanged) {
        generatorConfigUpdate.includedCodeTypes = selectedTypes;
      }

      if (deptDeltaApplied) {
        generatorConfigUpdate.codeToClinicalDepartment = { ...clinicalDepartments.changes };
      }

      if (defaultDeptChanged) {
        generatorConfigUpdate.defaultClinicalDepartment = clinicalDepartments.defaultCode;
      }

      if (letterDeltaApplied) {
        generatorConfigUpdate.codeToLetterType = { ...letterTypes.changes };
      }

      if (defaultLetterChanged) {
        generatorConfigUpdate.defaultLetterTypeCode = letterTypes.defaultCode;
      }

      if (shouldExtractExtendedMeasurementsChanged) {
        generatorConfigUpdate.shouldExtractExtendedMeasurements = shouldExtractExtendedMeasurements;
      }

      if (shouldBundleBloodPressureReadingsChanged) {
        generatorConfigUpdate.shouldBundleBloodPressureReadings = shouldBundleBloodPressureReadings;
      }

      if (measurementDeltaApplied) {
        generatorConfigUpdate.measurementTypeToCode = measurements.measurementChanges;
      }

      if (guidanceDeltaApplied) {
        generatorConfigUpdate.documentContentTypeToGuidance = { ...guidanceChanges };
      }

      const generatorConfigChanged =
        typesChanged ||
        measurementDeltaApplied ||
        deptDeltaApplied ||
        defaultDeptChanged ||
        letterDeltaApplied ||
        defaultLetterChanged ||
        shouldExtractExtendedMeasurementsChanged ||
        shouldBundleBloodPressureReadingsChanged ||
        guidanceDeltaApplied;

      if (generatorConfigChanged) {
        updateData.generatorConfig = generatorConfigUpdate;
      }

      // If nothing changed anywhere, don't make the request
      if (!documentUpdate.changed && !generatorConfigChanged) {
        setIsEditingConfig(false);
        setSaving(false);
        return;
      }

      const updatedConfig = await configManager.updateConfig(updateData);

      if (updatedConfig) {
        applySavedConfig(updateData, true);
      } else {
        applySavedConfig(updateData, false);
      }

      const savedGuidance = guidanceDeltaApplied
        ? mergeCodeMapWithDelta(
            previousState.documentContentTypeToGuidance || {},
            guidanceChanges
          )
        : { ...documentContentTypeToGuidance };
      if (guidanceDeltaApplied) {
        setDocumentContentTypeToGuidance(savedGuidance);
      }

      setPreviousState({
        retentionDays: documentSection.retentionDays,
        emailJoinType: documentSection.emailJoinType,
        shouldIncludeReceivedAt: documentSection.shouldIncludeReceivedAt,
        shouldSendEmailSuccessAck: documentSection.shouldSendEmailSuccessAck,
        shouldExtractExtendedMeasurements,
        shouldBundleBloodPressureReadings,
        defaultDocumentTypeField: documentSection.defaultDocumentTypeField,
        selectedTypes: [...selectedTypes],
        measurementCodes: { ...measurements.measurementCodes },
        clinicalDepartmentCodes: { ...clinicalDepartments.codes },
        defaultClinicalDepartment: clinicalDepartments.defaultCode
          ? { ...clinicalDepartments.defaultCode, terms: [...(clinicalDepartments.defaultCode.terms || [])] }
          : null,
        letterTypeCodes: { ...letterTypes.codes },
        defaultLetterTypeCode: letterTypes.defaultCode
          ? { ...letterTypes.defaultCode, terms: [...(letterTypes.defaultCode.terms || [])] }
          : null,
        documentContentTypeToGuidance: savedGuidance,
        titleComponents: JSON.parse(JSON.stringify(documentSection.titleComponents))
      });

      measurements.clearChanges();
      clinicalDepartments.clearChanges();
      letterTypes.clearChanges();
      setGuidanceChanges({});

      setIsEditingConfig(false);
      documentSection.setEditingTitleComponent(null);
      setSaving(false);
      addNotification('config-save', 'Configuration saved successfully');
    } catch (err) {
      console.error('Error saving configuration:', err);
      addNotification('config-save-error', err.message || 'Failed to save configuration', { isError: true });
      setSaving(false);

      // Re-fetch config to ensure we're in sync with the server
      fetchConfig();
    }
  };

  return {
    config,
    loading,
    saving,
    error,
    isEditingConfig,
    selectedTypes,
    shouldExtractExtendedMeasurements,
    setShouldExtractExtendedMeasurements,
    shouldBundleBloodPressureReadings,
    setShouldBundleBloodPressureReadings,
    documentContentTypeToGuidance,
    isTemplateManagerModalOpen,
    setIsTemplateManagerModalOpen,
    documentSection,
    clinicalDepartments,
    letterTypes,
    measurements,
    handleCodeSearch,
    handleGuidanceChange,
    handleGuidanceClear,
    toggleSearchType,
    handleEditToggle,
    handleCancel,
    handleSave,
    fetchConfig,
  };
};

export default useConfigForm;
